import { useMemo, CSSProperties, useCallback } from 'react';
import classNames from 'classnames';
import { useTheme, Stack, Button } from '@mui/material';
import { useTranslation } from '@/hooks/translations';
import { reduceOpacity } from '@/services/utils/color_manipulate';

import styles from './index.module.scss';
import SVGProgressIndicator from './ProcessIndicator';

interface IProps {
    totalPages: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    onDone: () => void;
}

const Pagination: React.FC<IProps> = ({ totalPages, currentPage, onPageChange, onDone }) => {
    const { t } = useTranslation('common');
    const theme = useTheme();

    const buttonConfigs = useMemo(() => {
        return [
            ...(totalPages > 1
                ? [
                      {
                          type: 'back',
                          text: t('Prev'),
                          textColor: theme.palette.primary.main,
                          color: reduceOpacity({ hex: theme.palette.primary.contrastText, opacity: 0.2 }),
                          disabled: currentPage === 0,
                          isDone: false,
                      },
                  ]
                : []),
            {
                type: 'next',
                text: currentPage === totalPages - 1 ? t('Done') : t('Next'),
                textColor: theme.palette.common.white,
                color: theme.palette.primary.main,
                disabled: currentPage === totalPages - 1,
                isDone: currentPage === totalPages - 1,
            },
        ] as const;
    }, [currentPage, totalPages]);

    const handlePageChange = useCallback(
        (page: number) => {
            if (page >= 0 && page < totalPages) {
                onPageChange?.(page);
            }
        },
        [totalPages],
    );

    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            direction="column"
            spacing={6}
            className={styles.container}
            style={
                {
                    '--min-scale': 'auto',
                    '--max-scale': 'auto',
                } as CSSProperties
            }
        >
            <Stack
                className={styles.fullWidth}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingLeft: '30px',
                    paddingRight: '30px',
                    paddingBottom: '10px',
                }}
            >
                <div className={styles.progressIndicatorContainer}>
                    <SVGProgressIndicator
                        totalSteps={totalPages}
                        currentStep={currentPage}
                        onStepChange={handlePageChange}
                    />
                </div>

                <div className={styles.buttonContainer}>
                    {buttonConfigs.map(({ type, text, color, disabled, textColor, isDone }) => {
                        if (!text) {
                            return null;
                        }

                        return (
                            <Button
                                key={type}
                                variant={type === 'back' ? 'outlined' : 'contained'}
                                fullWidth
                                onClick={() => {
                                    if (isDone) {
                                        onDone();
                                    } else if (type === 'next') {
                                        handlePageChange(currentPage + 1);
                                    } else if (type === 'back') {
                                        handlePageChange(currentPage - 1);
                                    }
                                }}
                                disabled={isDone ? false : disabled}
                                className={classNames(styles.navigationButton, {
                                    [styles.disabledButton]: isDone ? false : disabled,
                                })}
                                style={
                                    {
                                        '--button-color': color,
                                        '--text-color': textColor,
                                        marginLeft: '10px',
                                        borderRadius: '12px',
                                        width: '100%',
                                        maxWidth: '150px',
                                    } as React.CSSProperties
                                }
                                aria-label={type === 'back' ? 'Previous page' : 'Next page'}
                            >
                                {text}
                            </Button>
                        );
                    })}
                </div>
            </Stack>
        </Stack>
    );
};

export default Pagination;
